import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Card, Image, Spinner } from 'react-bootstrap';
import { FiChevronRight } from 'react-icons/fi';
import { useTenant } from '../../context/TenantContext';
import { getOrganizationCardLabels } from '../../platform/organizationLabels';
import { resolveBrandingColors } from '../../platform/brandingColors';
import { loadSelectableOrganizations } from '../../services/tenantsService';
import './SelectOrganization.css';

function OrganizationCard({ organization, loadingId, onSelect }) {
  const { tenantId, config, error } = organization;
  const { branding } = config || {};
  const { shortTitle, subtitle } = getOrganizationCardLabels(config);
  const { primaryColor, accentColor } = resolveBrandingColors(branding);
  const isLoading = loadingId === tenantId;
  const isDisabled = Boolean(error) || isLoading;

  return (
    <Card
      className={`org-card${error ? ' org-card--error' : ''}${isDisabled ? ' org-card--disabled' : ''}`}
      onClick={() => !isDisabled && onSelect(tenantId)}
      role="button"
      tabIndex={isDisabled ? -1 : 0}
      aria-label={shortTitle}
      onKeyDown={(event) => {
        if (!isDisabled && (event.key === 'Enter' || event.key === ' ')) {
          event.preventDefault();
          onSelect(tenantId);
        }
      }}
    >
      <div className="org-card-accent" style={{ backgroundColor: accentColor }} />
      <Card.Body className="org-card-body">
        {error ? (
          <Alert variant="warning" className="mb-0 py-2 small">
            {error}
          </Alert>
        ) : (
          <>
            <div className="org-card-logo">
              {branding?.logoPath ? (
                <Image
                  src={branding.logoPath}
                  alt={branding.logoAlt || shortTitle}
                  className="org-card-logo-img"
                />
              ) : (
                <Spinner animation="border" size="sm" />
              )}
            </div>
            <div className="org-card-text">
              <div className="org-card-title" style={{ color: primaryColor }}>
                {shortTitle}
              </div>
              {subtitle && <div className="org-card-subtitle">{subtitle}</div>}
            </div>
            <FiChevronRight className="org-card-chevron" aria-hidden="true" />
            {isLoading && (
              <div className="org-card-loading">
                <Spinner animation="border" size="sm" />
              </div>
            )}
          </>
        )}
      </Card.Body>
    </Card>
  );
}

function SelectOrganization() {
  const navigate = useNavigate();
  const { switchTenant, clearSelectedOrganization } = useTenant();
  const [organizations, setOrganizations] = useState([]);
  const [pageLoading, setPageLoading] = useState(true);
  const [loadingId, setLoadingId] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    clearSelectedOrganization();
  }, [clearSelectedOrganization]);

  useEffect(() => {
    let cancelled = false;
    loadSelectableOrganizations()
      .then((loaded) => {
        if (!cancelled) {
          setOrganizations(loaded);
        }
      })
      .catch((loadError) => {
        if (!cancelled) {
          setError(loadError?.message || 'Nalaganje organizacij ni uspelo');
        }
      })
      .finally(() => {
        if (!cancelled) {
          setPageLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const handleSelect = async (tenantId) => {
    setLoadingId(tenantId);
    setError('');
    try {
      await switchTenant(tenantId);
      navigate('/login');
    } catch (switchError) {
      setError(switchError?.message || 'Izbira organizacije ni uspela');
    } finally {
      setLoadingId(null);
    }
  };

  return (
    <div className="org-container">
      <div className="org-panel">
        <header className="org-panel-header">
          <h1 className="org-title">Izberite organizacijo</h1>
          <p className="org-subtitle">
            Izberite sindikat, v katerega se želite prijaviti.
          </p>
        </header>

        {error && <Alert variant="danger" className="org-error">{error}</Alert>}

        {pageLoading ? (
          <div className="org-loading">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Nalagam...</span>
            </Spinner>
          </div>
        ) : (
          <div className="org-list" role="list">
            {organizations.map((organization) => (
              <OrganizationCard
                key={organization.tenantId}
                organization={organization}
                loadingId={loadingId}
                onSelect={handleSelect}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default SelectOrganization;
